import type { AppSettings, AutomaticFundData, Fund, MarketSignal, Period, ScoringWeights, WeightKey } from './types'

export type MetricBreakdown = { key: WeightKey; label: string; score: number; weight: number; contribution: number; available: boolean; note: string }

export type FundAnalysis = {
  momentum: number
  momentumLabel: string
  momentumDirection: 'accelerating' | 'decelerating' | 'reversal' | 'mixed' | 'steady'
  momentumNote: string
  marketScore: number
  marketLabel: string
  marketSignal?: MarketSignal
  relativeScore: number
  riskAdjustedScore: number
  riskScore: number
  riskLabel: string
  managerScore: number
  cashFlowScore: number
  valuationScore: number
  diversificationScore: number
  concentration: { top5: number; top10: number; largest: number; sector: number; geography: number; currency: number; flags: string[] }
  qualityScore: number
  entryScore: number
  recommendation: string
  recommendationLevel: 'positive' | 'neutral' | 'negative'
  recommendationNote: string
  decisionGuard?: { status: 'clear' | 'caution' | 'blocked'; title: string; reasons: string[] }
  confidence: 'high' | 'medium' | 'low'
  confidencePercent: number
  dataQuality: number
  breakdown: MetricBreakdown[]
  positives: string[]
  risks: string[]
  watchItems: string[]
  entryConsiderations: string[]
  exitTriggers: string[]
  effectiveSharpe: number | null
  effectiveSortino: number | null
  sharpeNote?: string
  sortinoNote?: string
  changes: { label: string; previous: string; current: string; direction: 'up' | 'down' | 'flat' }[]
}

const momentumWeights: Record<'1m' | '3m' | '6m' | '1y', number> = { '1m': 35, '3m': 30, '6m': 20, '1y': 15 }

export const metricLabels: Record<WeightKey, string> = {
  market: 'Market attractiveness', relative: 'Relative performance', momentum: 'Momentum', riskAdjusted: 'Risk-adjusted return', manager: 'Manager quality', cashFlow: 'Cash flow', valuation: 'Valuation', diversification: 'Diversification',
}

export const periodsForTable: { id: Period; label: string }[] = [
  { id: '1d', label: '1D' }, { id: '1w', label: '1W' }, { id: '1m', label: '1M' }, { id: '3m', label: '3M' }, { id: '6m', label: '6M' }, { id: 'ytd', label: 'YTD' }, { id: '1y', label: '1Y' }, { id: '3y', label: '3Y' }, { id: '5y', label: '5Y' },
]

export function clamp(value: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min))
}

export function safe(value: number | null | undefined, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

export function average(values: (number | null | undefined)[]): number | null {
  const present = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  return present.length ? present.reduce((sum, value) => sum + value, 0) / present.length : null
}

export function formatNumber(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function formatPct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${value >= 0 ? '+' : ''}${formatNumber(value, digits)}%`
}

export function formatTRY(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: digits }).format(value)
}

export function shortNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`
  return value.toFixed(value % 1 ? 1 : 0)
}

function rankPercentile(value: number | null, peers: (number | null | undefined)[], higherIsBetter = true): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 50
  const values = peers.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
  if (values.length < 2) return clamp(50 + value * 0)
  const rank = values.filter((item) => item < value).length + values.filter((item) => item === value).length * 0.5
  const percentile = (rank / values.length) * 100
  return clamp(higherIsBetter ? percentile : 100 - percentile)
}

function rawFallback(value: number | null, scale = 1): number {
  if (value === null || !Number.isFinite(value)) return 50
  return clamp(50 + value * scale)
}

function categoryPeers(fund: Fund, funds: Fund[]): Fund[] {
  const peers = funds.filter((item) => item.category === fund.category)
  return peers.length > 1 ? peers : [fund]
}

function automaticPeers(fund: Fund, funds: Fund[]): Fund[] {
  const key = fund.automatic?.theme || fund.category
  const peers = funds.filter((item) => (item.automatic?.theme || item.category) === key)
  return peers.length > 1 ? peers : [fund]
}

function metricPercentile(value: number | null, peers: (number | null | undefined)[], higherIsBetter = true, fallbackScale?: number): number {
  const present = peers.filter((item): item is number => typeof item === 'number' && Number.isFinite(item))
  return present.length >= 2 ? rankPercentile(value, present, higherIsBetter) : rawFallback(value, fallbackScale ?? (higherIsBetter ? 1 : -1))
}

function effectiveSharpe(fund: Fund): number | null {
  if (fund.sharpe.override && fund.sharpe.fund !== null) return fund.sharpe.fund
  if (fund.sharpe.annualizedReturn !== null && fund.sharpe.riskFreeRate !== null && fund.sharpe.volatility && fund.sharpe.volatility > 0) {
    return (fund.sharpe.annualizedReturn - fund.sharpe.riskFreeRate) / fund.sharpe.volatility
  }
  return fund.sharpe.fund
}

function effectiveSortino(fund: Fund): number | null {
  if (fund.sortino.override && fund.sortino.fund !== null) return fund.sortino.fund
  if (fund.sortino.annualizedReturn !== null && fund.sortino.targetReturn !== null && fund.sortino.downsideDeviation && fund.sortino.downsideDeviation > 0) {
    return (fund.sortino.annualizedReturn - fund.sortino.targetReturn) / fund.sortino.downsideDeviation
  }
  return fund.sortino.fund
}

function cashScore(fund: Fund): number {
  const flow = safe(fund.cashFlow.monthly, 0)
  const size = safe(fund.cashFlow.fundSizeChange, 0)
  const investors = safe(fund.cashFlow.investorCountChange, 0)
  const score = 50 + flow * 0.8 + size * 0.7 + investors * 0.4
  return clamp(score)
}

function calculateConcentration(fund: Fund, settings: AppSettings): FundAnalysis['concentration'] {
  const sorted = [...fund.holdings].sort((a, b) => b.weight - a.weight)
  const top5 = sorted.slice(0, 5).reduce((sum, item) => sum + item.weight, 0)
  const top10 = sorted.slice(0, 10).reduce((sum, item) => sum + item.weight, 0)
  const largest = sorted[0]?.weight ?? 0
  const maxBucket = (items: { name: string; value: number }[]) => Math.max(0, ...items.map((item) => item.value))
  const sector = maxBucket(fund.exposure.sector)
  const geography = maxBucket(fund.exposure.geography)
  const currency = maxBucket(fund.exposure.currency)
  const flags: string[] = []
  if (top5 > settings.thresholds.topHoldings) flags.push(`Top 5 holdings are ${formatNumber(top5)}% of the portfolio.`)
  if (sector > settings.thresholds.sector) flags.push(`${fund.exposure.sector.find((item) => item.value === sector)?.name ?? 'One sector'} is ${formatNumber(sector)}% of exposure.`)
  if (largest > settings.thresholds.singleHolding) flags.push(`Largest holding is ${formatNumber(largest)}%, above the configured ${settings.thresholds.singleHolding}% threshold.`)
  const score = clamp(100 - Math.max(0, top5 - settings.thresholds.topHoldings) * 2 - Math.max(0, sector - settings.thresholds.sector) * 1.5 - Math.max(0, largest - settings.thresholds.singleHolding) * 2)
  return { top5, top10, largest, sector, geography, currency, flags: [...flags, `Diversification score contribution: ${formatNumber(score, 0)}/100.`] }
}

function marketStatus(score: number): string {
  if (score >= 78) return 'strong bullish'
  if (score >= 60) return 'bullish'
  if (score >= 42) return 'neutral'
  if (score >= 24) return 'bearish'
  return 'strong bearish'
}

function riskLabel(volatility: number | null): string {
  if (volatility === null) return 'not enough data'
  if (volatility < 3) return 'very low'
  if (volatility < 10) return 'low'
  if (volatility < 20) return 'moderate'
  if (volatility < 30) return 'high'
  return 'very high'
}

function momentumLabel(score: number): string {
  if (score >= 80) return 'very strong momentum'
  if (score >= 65) return 'strong momentum'
  if (score >= 50) return 'neutral-positive'
  if (score >= 35) return 'weakening momentum'
  if (score >= 20) return 'weak momentum'
  return 'strong negative momentum'
}

function recommendationFor(score: number, entryScore: number, warningCount: number): string {
  let adjusted = score - Math.min(12, warningCount * 4)
  if (entryScore < 40 && adjusted > 60) adjusted -= 4
  if (adjusted >= 90) return 'strong candidate'
  if (adjusted >= 80) return 'buy candidate'
  if (adjusted >= 70) return 'buy candidate'
  if (adjusted >= 60) return 'hold'
  if (adjusted >= 50) return 'neutral'
  if (adjusted >= 40) return 'avoid new position'
  if (adjusted >= 30) return 'reduce'
  return 'strong avoid'
}

function recommendationLevel(label: string): FundAnalysis['recommendationLevel'] {
  if (label.includes('candidate') || label === 'hold') return 'positive'
  if (label === 'neutral') return 'neutral'
  return 'negative'
}

function dataQuality(fund: Fund, peers: Fund[], settings: AppSettings): number {
  const values: (number | string | null | undefined)[] = [
    fund.latestPrice > 0 ? fund.latestPrice : null, fund.fundSize > 0 ? fund.fundSize : null, fund.investors > 0 ? fund.investors : null, fund.returns['1m'].fund, fund.returns['3m'].fund, fund.returns['1y'].fund,
    fund.volatility.y1, fund.drawdown.max, effectiveSharpe(fund), effectiveSortino(fund), fund.downsideRisk.fund, fund.beta.value, fund.alpha.value,
    fund.cashFlow.monthly, fund.exposure.asset.length ? 1 : null, fund.exposure.sector.length ? 1 : null, fund.marketDrivers.length ? 1 : null,
    fund.thesis.whyOwn, fund.history.length > 1 ? 1 : null,
  ]
  const completion = values.filter((value) => value !== null && value !== undefined && value !== '').length / values.length
  const days = Math.max(0, Math.round((new Date().getTime() - new Date(fund.lastUpdated).getTime()) / 86400000))
  const freshness = days <= settings.freshness.freshDays ? 1 : days <= settings.freshness.agingDays ? 0.78 : 0.48
  const peerSupport = peers.length > 1 ? 1 : 0.7
  return Math.round(clamp(completion * freshness * peerSupport * 100))
}

function difference(previous: number | null | undefined, current: number | null | undefined, label: string, suffix = ''): FundAnalysis['changes'][number] {
  const oldValue = typeof previous === 'number' ? previous : null
  const newValue = typeof current === 'number' ? current : null
  const direction = oldValue === null || newValue === null || Math.abs(newValue - oldValue) < 0.05 ? 'flat' : newValue > oldValue ? 'up' : 'down'
  return { label, previous: oldValue === null ? '—' : `${formatNumber(oldValue)}${suffix}`, current: newValue === null ? '—' : `${formatNumber(newValue)}${suffix}`, direction }
}

function scoreWithWeights(scores: Partial<Record<WeightKey, number>>, weights: ScoringWeights): { score: number; breakdown: MetricBreakdown[] } {
  const entries = (Object.keys(weights) as WeightKey[]).map((key) => ({ key, score: scores[key], weight: weights[key] })).filter((item): item is { key: WeightKey; score: number; weight: number } => typeof item.score === 'number' && Number.isFinite(item.score) && item.weight > 0)
  const totalWeight = entries.reduce((sum, item) => sum + item.weight, 0) || 1
  const breakdown = entries.map((item) => ({ key: item.key, label: metricLabels[item.key], score: item.score, weight: (item.weight / totalWeight) * 100, contribution: item.score * (item.weight / totalWeight), available: true, note: 'Category-relative where peer data is available.' }))
  return { score: Math.round(breakdown.reduce((sum, item) => sum + item.contribution, 0)), breakdown }
}

function automaticMomentumDirection(a: AutomaticFundData['analytics'], recentWeekly: number | null): FundAnalysis['momentumDirection'] {
  if (a.r1m !== null && a.r1m < 0 && a.r3m !== null && a.r3m > 0) return 'reversal'
  if (recentWeekly !== null && recentWeekly <= -1) return 'decelerating'
  if (a.momentum !== null && a.momentum >= 0.5) return 'accelerating'
  if (a.momentum !== null && a.momentum <= -0.5) return 'decelerating'
  if (a.r1m !== null && a.r3m !== null) {
    const recentPace = a.r1m
    const mediumPace = a.r3m / 3
    if (recentPace < mediumPace * 0.95) return 'decelerating'
    if (recentPace > mediumPace * 1.05) return 'accelerating'
  }
  if (a.momentum === 0) return 'mixed'
  return 'steady'
}

function automaticRiskAdjustedScore(value: number | null, higherIsBetter = true): number | null {
  if (value === null) return null
  const mapped = higherIsBetter ? 50 + value * 25 : 50 - value * 2
  return clamp(mapped)
}

function automaticQualityScore(parts: Array<{ key: WeightKey; label: string; score: number | null; weight: number; note: string }>, sampleSize: number): { score: number; breakdown: MetricBreakdown[] } {
  const present = parts.filter((item): item is typeof item & { score: number } => typeof item.score === 'number' && Number.isFinite(item.score))
  if (!present.length) return { score: 50, breakdown: [] }
  // Short NAV histories can produce extreme ranks and ratios by chance. Shrink
  // the evidence toward neutral until a roughly six-month daily history exists.
  const historyReliability = Math.min(1, Math.max(0, sampleSize) / 120)
  const totalWeight = present.reduce((sum, item) => sum + item.weight, 0) || 1
  const breakdown = present.map((item) => {
    const adjustedScore = clamp(50 + (item.score - 50) * historyReliability)
    const weight = item.weight / totalWeight * 100
    return {
      key: item.key,
      label: item.label,
      score: adjustedScore,
      weight,
      contribution: adjustedScore * weight / 100,
      available: true,
      note: `${item.note}${historyReliability < 1 ? ` History reliability ${Math.round(historyReliability * 100)}%; score is shrunk toward 50.` : ''}`,
    }
  })
  return { score: Math.round(breakdown.reduce((sum, item) => sum + item.contribution, 0)), breakdown }
}

function automaticMomentumScore(a: AutomaticFundData['analytics'], peers: Fund[], recentWeekly: number | null, direction: FundAnalysis['momentumDirection']): number {
  const inputs = [
    { value: a.r1m, peers: peers.map((peer) => peer.automatic?.analytics.r1m ?? null), weight: 35 },
    { value: a.r3m, peers: peers.map((peer) => peer.automatic?.analytics.r3m ?? null), weight: 30 },
    { value: a.r6m, peers: peers.map((peer) => peer.automatic?.analytics.r6m ?? null), weight: 20 },
    { value: a.r1y, peers: peers.map((peer) => peer.automatic?.analytics.r1y ?? null), weight: 15 },
  ]
  const present = inputs.filter((item) => item.value !== null)
  const periodScore = present.length
    ? present.reduce((sum, item) => sum + metricPercentile(item.value, item.peers, true, 1.8) * item.weight, 0) / present.reduce((sum, item) => sum + item.weight, 0)
    : null
  const providerScore = a.momentum === null ? null : clamp(50 + a.momentum * 50)
  let score = periodScore === null ? (providerScore ?? 50) : providerScore === null ? periodScore : periodScore * 0.75 + providerScore * 0.25

  // A high cross-sectional rank must not hide a current reversal. These caps
  // keep the visible momentum card consistent with the latest direction.
  if (recentWeekly !== null && recentWeekly < -0.5) score = Math.min(score, 59)
  if (recentWeekly !== null && recentWeekly <= -2) score = Math.min(score, 44)
  if (recentWeekly !== null && recentWeekly < 0 && a.r6m !== null && a.r6m < 0) score = Math.min(score, 39)
  if (direction === 'reversal') score = Math.min(score, 34)
  else if (direction === 'decelerating') score = Math.min(score, recentWeekly !== null && recentWeekly <= -2 ? 44 : 59)
  else if (direction === 'mixed') score = Math.min(score, 64)
  return Math.round(clamp(score))
}

function automaticMarketProxyScore(a: AutomaticFundData['analytics'], phase: string | null, momentumDirection: FundAnalysis['momentumDirection'], recentWeekly: number | null, marketSignal?: MarketSignal): number {
  let fundTrendScore = a.trend === null ? 50 : clamp(50 + a.trend * 2.5)
  // Being far above the long-term average confirms direction, but it is not
  // the same thing as an attractive fresh entry. Cap the fund component before
  // blending it with an independent market series.
  if (a.trend !== null && a.trend > 12) fundTrendScore = Math.min(fundTrendScore, 70 - Math.min(20, (a.trend - 12) * 1.5))
  let score = fundTrendScore
  if (marketSignal) {
    let underlyingScore = marketSignal.trendPct === null ? 50 : clamp(50 + marketSignal.trendPct * 2.5)
    if (marketSignal.change1w !== null && marketSignal.change1w < -0.5) underlyingScore -= 5
    if (marketSignal.change1w !== null && marketSignal.change1w <= -3) underlyingScore -= 15
    score = fundTrendScore * 0.45 + clamp(underlyingScore) * 0.55
  }
  if (phase === 'distribution') score -= 12
  if (phase === 'markdown') score -= 25
  if (momentumDirection === 'reversal') score -= 20
  else if (momentumDirection === 'decelerating') score -= 10
  else if (momentumDirection === 'mixed') score -= 6
  if (recentWeekly !== null && recentWeekly < -0.5) score -= 5
  return Math.round(clamp(score))
}

function thematicMandate(fund: Fund): string | null {
  const text = fund.name.toLocaleUpperCase('tr-TR')
  if (/GÜMÜŞ|GUMUS|SILVER/.test(text)) return 'silver / precious metals'
  if (fund.automatic?.theme === 'gold' || /ALTIN|GOLD|KIYMETLİ MADEN|KIYMETLI MADEN/.test(text)) return 'gold / precious metals'
  if (/İLAÇ|ILAC|MEDİKAL|MEDIKAL|TIBB[Iİ]|SAĞLIK|SAGLIK|HEALTH|PHARMA/.test(text)) return 'healthcare / medical'
  if (/BANKACILIK|BANKA/.test(text)) return 'banking'
  if (/SINA[Iİ]|ENDÜSTR[Iİ]|SANAY[Iİ]/.test(text)) return 'industrial'
  if (/TEKNOLOJ|YAZILIM|BİLİŞİM|BILISIM/.test(text)) return 'technology'
  if (/ENERJ[Iİ]|ELEKTR[Iİ]K/.test(text)) return 'energy'
  return null
}

function marketSignalForFund(fund: Fund, signals: MarketSignal[]): MarketSignal | undefined {
  const text = fund.name.toLocaleUpperCase('tr-TR')
  const theme = fund.automatic?.theme
  const id = /GÜMÜŞ|GUMUS|SILVER/.test(text)
    ? 'silver'
    : theme === 'gold' || /ALTIN|GOLD|KIYMETLİ MADEN|KIYMETLI MADEN/.test(text)
      ? 'gold'
      : /BANKACILIK|BANKA/.test(text)
        ? 'xbank'
        : /SINA[Iİ]|ENDÜSTR[Iİ]|SANAY[Iİ]/.test(text)
          ? 'xusin'
          : /TEKNOLOJ|YAZILIM|BİLİŞİM|BILISIM/.test(text)
            ? 'xutek'
            : theme === 'equity_tr'
              ? 'bist100'
              : null
  return id ? signals.find((signal) => signal.id === id && signal.available) : undefined
}

function hasCurrentProviderProxy(a: AutomaticFundData['analytics'], advanced: AutomaticFundData['advanced']): boolean {
  const sampleSize = Math.max(a.sampleSize, advanced.sampleSize || 0)
  return sampleSize >= 60 && a.momentum !== null && a.trend !== null
}

function automaticDecisionGuard(fund: Fund, a: AutomaticFundData['analytics'], advanced: AutomaticFundData['advanced'], momentumDirection: FundAnalysis['momentumDirection'], marketScore: number, recentWeekly: number | null, currentProviderProxy: boolean, marketSignal?: MarketSignal): NonNullable<FundAnalysis['decisionGuard']> {
  const reasons: string[] = []
  let status: 'clear' | 'caution' | 'blocked' = 'clear'
  const sector = thematicMandate(fund)
  const hasSectorData = fund.exposure.sector.length > 0 || fund.holdings.length > 0
  const currentDrawdown = fund.drawdown.current ?? advanced.drawdownNow
  const sampleSize = Math.max(a.sampleSize, advanced.sampleSize || 0)
  const extended = a.trend !== null && a.trend >= 12
  const severeRecentDrop = recentWeekly !== null && recentWeekly <= -3
  const severeMomentum = momentumDirection === 'reversal' || (momentumDirection === 'decelerating' && recentWeekly !== null && recentWeekly <= -2)
  const brokenTrend = advanced.phase === 'markdown' || marketScore < 35

  if (recentWeekly === null && !currentProviderProxy) {
    status = 'caution'
    reasons.push('The latest 1W NAV return and a refreshed provider trend/momentum proxy are unavailable; the engine will not call a fresh buy without a current signal.')
  }
  if (recentWeekly !== null && recentWeekly < -0.5) {
    status = 'caution'
    reasons.push(`Latest 1W return is ${formatPct(recentWeekly)}; the current move is negative.`)
  }
  if (momentumDirection === 'reversal' || momentumDirection === 'decelerating' || momentumDirection === 'mixed') {
    status = 'caution'
    reasons.push(momentumDirection === 'mixed' ? 'Short-term and medium-term performance disagree; the long-term score is not a fresh-buy signal.' : 'Recent momentum is weakening; positive long-term returns are lagging the latest direction.')
  }
  if (sector && !hasSectorData && !marketSignal) {
    status = 'caution'
    const confirmation = sector.includes('precious metals') ? 'underlying metal-market confirmation' : `${sector} sector/index confirmation`
    reasons.push(`Current ${confirmation} is unavailable; historical fund quality cannot confirm a new entry.`)
  }
  if (marketSignal?.change1w !== null && marketSignal?.change1w !== undefined && marketSignal.change1w <= -3) {
    status = 'blocked'
    reasons.push(`${marketSignal.name} is down ${formatNumber(Math.abs(marketSignal.change1w))}% over 1W; the underlying market move is too weak for a new entry.`)
  }
  if (sampleSize < 60) {
    status = 'caution'
    reasons.push(`Only ${sampleSize || 0} NAV observations are available; the historical evidence is too short for a confident fresh-buy label.`)
  }
  if (a.trend === null) {
    status = 'caution'
    reasons.push('The long-term trend proxy is unavailable, so current market direction cannot be confirmed.')
  }
  if (extended) {
    status = 'caution'
    reasons.push(`The fund is ${formatNumber(a.trend)}% above its long-term average; that confirms a strong trend but also signals an extended entry.`)
  }
  if (currentDrawdown !== null && currentDrawdown <= -2) {
    status = 'caution'
    reasons.push(`Current drawdown is ${formatPct(currentDrawdown)} from the recent high.`)
  }
  if (currentDrawdown !== null && currentDrawdown <= -10) {
    status = 'caution'
    reasons.push('The fund is in a deep current drawdown; a high historical score is not evidence that the decline has ended.')
  }
  if (severeRecentDrop || severeMomentum || brokenTrend) {
    status = 'blocked'
    if (brokenTrend) reasons.push('The current trend proxy is no longer supportive.')
    else reasons.push('The recent decline is large enough to block a new-buy recommendation until the trend stabilizes.')
  }
  if (currentDrawdown !== null && currentDrawdown <= -15 && recentWeekly !== null && recentWeekly < 0) {
    status = 'blocked'
    reasons.push('Deep drawdown and a negative latest week are aligned against a new entry; wait for stabilization.')
  }
  const uniqueReasons = [...new Set(reasons)]
  return {
    status,
    title: status === 'blocked' ? 'NEW BUY BLOCKED' : status === 'caution' ? 'WAIT FOR CURRENT-SIGNAL CONFIRMATION' : 'CURRENT SIGNALS ALIGNED',
    reasons: uniqueReasons,
  }
}

function automaticAnalysis(fund: Fund, auto: AutomaticFundData, funds: Fund[], marketSignals: MarketSignal[]): FundAnalysis {
  const a = auto.analytics
  const fc = auto.forecast
  const peers = automaticPeers(fund, funds)
  const marketSignal = marketSignalForFund(fund, marketSignals)
  const peerVolatilityScore = metricPercentile(a.volatility1y, peers.map((peer) => peer.automatic?.analytics.volatility1y ?? null), false, -1.7)
  const peerDrawdownScore = metricPercentile(a.maxDrawdown1y, peers.map((peer) => peer.automatic?.analytics.maxDrawdown1y ?? null), true, 1.2)
  const peerSharpeScore = a.sharpe1y === null ? null : metricPercentile(a.sharpe1y, peers.map((peer) => peer.automatic?.analytics.sharpe1y ?? null), true, 8)
  const peerSortinoScore = a.sortino1y === null ? null : metricPercentile(a.sortino1y, peers.map((peer) => peer.automatic?.analytics.sortino1y ?? null), true, 3)
  const peerConsistencyScore = a.consistency === null ? null : metricPercentile(a.consistency, peers.map((peer) => peer.automatic?.analytics.consistency ?? null), true, 1)
  const peerAlphaScore = fc?.alphaTheme === null || fc?.alphaTheme === undefined ? null : metricPercentile(fc.alphaTheme, peers.map((peer) => peer.automatic?.forecast?.alphaTheme ?? null), true, 2)
  const recentWeekly = fund.returns['1w'].fund
  const currentProviderProxy = recentWeekly === null && hasCurrentProviderProxy(a, auto.advanced)
  const currentSignalAvailable = recentWeekly !== null || currentProviderProxy
  const momentumDirection = automaticMomentumDirection(a, recentWeekly)
  const momentum = automaticMomentumScore(a, peers, recentWeekly, momentumDirection)
  const momentumNote = momentumDirection === 'accelerating'
    ? 'The automatic NAV engine detects improving short-term momentum.'
    : momentumDirection === 'decelerating' || momentumDirection === 'reversal'
      ? 'Longer-term performance may remain positive, but the latest automatic trend signal is weakening.'
      : momentumDirection === 'mixed'
        ? 'Short-term and medium-term signals disagree; a strong historical score is not enough to justify a fresh entry.'
      : 'Short- and medium-term automatic signals are broadly aligned.'
  const marketScore = automaticMarketProxyScore(a, auto.advanced.phase, momentumDirection, recentWeekly, marketSignal)
  const marketLabel = marketStatus(marketScore)
  const volScore = a.volatility1y === null ? 50 : peerVolatilityScore
  const ddScore = a.maxDrawdown1y === null ? 50 : peerDrawdownScore
  const riskScore = Math.round((volScore * 0.55) + (ddScore * 0.45))
  const sharpeScoreRaw = automaticRiskAdjustedScore(a.sharpe1y)
  const sortinoScoreRaw = automaticRiskAdjustedScore(a.sortino1y)
  const riskAdjustedScore = Math.round(average([peerSharpeScore, peerSortinoScore]) ?? 50)
  const relativeScore = Math.round(clamp(a.peerPercentile1y ?? 50))
  const managerScore = Math.round((relativeScore * 0.45) + ((peerConsistencyScore ?? 50) * 0.35) + (peerAlphaScore ?? 50) * 0.2)
  // The full universe payload does not carry a per-fund 1W return. When the
  // provider has a sufficiently long, freshly computed trend/momentum series,
  // use that as a weaker current proxy instead of turning every fund into a
  // false "missing data" rejection. Direct TEFAS history still wins whenever
  // it is loaded for the selected fund.
  const providerProxyScore = a.momentum === null ? 50 : clamp(50 + a.momentum * 25)
  const recentWeekScore = recentWeekly === null ? currentProviderProxy ? providerProxyScore : 50 : clamp(50 + recentWeekly * 8)
  const extensionPenalty = a.trend !== null && a.trend > 12 ? Math.min(12, (a.trend - 12) * 1.5) : 0
  const pullbackPenalty = (fund.drawdown.current ?? auto.advanced.drawdownNow ?? 0) < -1 ? Math.min(7, Math.abs(fund.drawdown.current ?? auto.advanced.drawdownNow ?? 0) * 2) : 0
  const momentumPenalty = momentumDirection === 'reversal' ? 16 : momentumDirection === 'decelerating' ? 8 : momentumDirection === 'mixed' ? 5 : 0
  const entryScore = Math.round(clamp((momentum * 0.32) + (marketScore * 0.23) + recentWeekScore * 0.2 + ddScore * 0.15 + riskAdjustedScore * 0.1 - extensionPenalty - pullbackPenalty - momentumPenalty))
  // Data quality is coverage, not a confidence claim. Current signals and
  // historical evidence are weighted separately; a missing sector feed is
  // especially important for a fund whose name advertises a narrow theme.
  const sectorDataAvailable = fund.exposure.sector.length > 0 || fund.holdings.length > 0 || Boolean(marketSignal)
  const sectorRequired = Boolean(thematicMandate(fund))
  const coverage = [
    { available: fund.latestPrice > 0 && currentSignalAvailable, weight: 20 },
    { available: a.r1y !== null && a.peerPercentile1y !== null, weight: 15 },
    { available: a.volatility1y !== null && a.maxDrawdown1y !== null, weight: 15 },
    { available: sharpeScoreRaw !== null || sortinoScoreRaw !== null, weight: 15 },
    { available: a.momentum !== null && a.trend !== null, weight: 15 },
    { available: fund.fundSize > 0 || fund.investors > 0, weight: 5 },
    { available: fund.exposure.asset.length > 0, weight: 5 },
    { available: !sectorRequired || sectorDataAvailable, weight: 10 },
  ]
  const coverageScore = coverage.filter((item) => item.available).reduce((sum, item) => sum + item.weight, 0)
  const historyScore = clamp((a.sampleSize / 120) * 100)
  let dataQuality = Math.round(coverageScore * 0.8 + historyScore * 0.2)
  if (sectorRequired && !sectorDataAvailable) dataQuality = Math.min(dataQuality, 80)
  if (!currentSignalAvailable) dataQuality = Math.min(dataQuality, 65)
  else if (currentProviderProxy) dataQuality = Math.min(dataQuality, 85)
  if (a.sampleSize < 60) dataQuality = Math.min(dataQuality, 60)
  const qualityResult = automaticQualityScore([
    { key: 'relative', label: '1Y peer percentile', score: a.peerPercentile1y, weight: 35, note: 'Automatic category/theme-relative rank.' },
    { key: 'riskAdjusted', label: 'Risk-adjusted return', score: average([peerSharpeScore, peerSortinoScore]), weight: 25, note: `Category-relative average of available Sharpe and Sortino percentiles (${peers.length} comparable funds); missing ratios are excluded.` },
    { key: 'riskAdjusted', label: 'Drawdown control', score: a.maxDrawdown1y === null ? null : ddScore, weight: 20, note: 'Automatic 1Y maximum drawdown mapping.' },
    { key: 'manager', label: 'Consistency', score: a.consistency, weight: 20, note: 'Share of positive rolling windows.' },
  ], a.sampleSize)
  const qualityScore = qualityResult.score
  const decisionGuard = automaticDecisionGuard(fund, a, auto.advanced, momentumDirection, marketScore, recentWeekly, currentProviderProxy, marketSignal)
  const recommendation = decisionGuard.status === 'blocked'
    ? (qualityScore >= 60 ? 'avoid new position' : 'strong avoid')
    : decisionGuard.status === 'caution'
      ? (qualityScore >= 65 ? 'hold' : qualityScore >= 45 ? 'neutral' : 'avoid new position')
      : qualityScore >= 80 && entryScore >= 70 && marketScore >= 60 && riskAdjustedScore >= 55 && dataQuality >= 65 && (momentumDirection === 'accelerating' || momentumDirection === 'steady')
        ? 'strong candidate'
        : qualityScore >= 70 && entryScore >= 60 && marketScore >= 52 && riskAdjustedScore >= 50
          ? 'buy candidate'
          : qualityScore >= 55
            ? 'hold'
            : qualityScore >= 40
              ? 'avoid new position'
              : 'reduce'
  const recommendationLevel: FundAnalysis['recommendationLevel'] = recommendation === 'strong avoid' || recommendation === 'avoid new position' || recommendation === 'reduce' ? 'negative' : recommendation === 'hold' || recommendation === 'neutral' ? 'neutral' : 'positive'
  const negativeFlags = auto.flags.filter((flag) => flag.severity === 'negative').map((flag) => flag.label)
  const cautionFlags = auto.flags.filter((flag) => flag.severity === 'caution').map((flag) => flag.label)
  const positiveFlags = auto.flags.filter((flag) => flag.severity === 'positive').map((flag) => flag.label)
  const positives = [...positiveFlags]
  if (relativeScore >= 65) positives.push(`Automatic peer percentile is ${relativeScore}/100.`)
  if (a.sharpe1y !== null && a.sharpe1y > 1) positives.push(`Automatic Sharpe is ${formatNumber(a.sharpe1y)} on the available NAV history.`)
  const risks = [...negativeFlags]
  if (riskScore < 45) risks.push(`Automatic risk score is ${riskScore}/100 with ${riskLabel(a.volatility1y)} volatility.`)
  if (a.maxDrawdown1y !== null && a.maxDrawdown1y <= -12) risks.push(`Maximum drawdown is ${formatPct(a.maxDrawdown1y)} on the automatic history.`)
  const watchItems = [...cautionFlags]
  if (decisionGuard.status === 'blocked') risks.unshift(...decisionGuard.reasons.slice(0, 2))
  else if (decisionGuard.status === 'caution') watchItems.unshift(...decisionGuard.reasons.slice(0, 2))
  if (a.sampleSize < 60) watchItems.push('Limited NAV history reduces confidence in the automatic ratios.')
  if (currentProviderProxy) watchItems.unshift('Exact 1W NAV is not included in the full-universe payload; this screening pass uses the provider’s refreshed trend/momentum proxy. Open the fund to confirm direct TEFAS 1W data before acting.')
  if (auto.advanced.phase && auto.advanced.phase !== 'unknown') watchItems.push(`Automatic market phase: ${auto.advanced.phase}.`)
  const reasoning = auto.reasoning.length ? auto.reasoning.slice(0, 2).join(' · ') : 'The automatic engine has not produced a narrative signal for this fund.'
  let confidencePercent = Math.round(clamp(auto.confidence || (a.sampleSize > 200 ? 78 : 52)))
  if (decisionGuard.status === 'caution') confidencePercent = Math.min(confidencePercent, 68)
  if (decisionGuard.status === 'blocked') confidencePercent = Math.min(confidencePercent, 55)
  if (currentProviderProxy) confidencePercent = Math.min(confidencePercent, 82)
  const confidence: FundAnalysis['confidence'] = confidencePercent >= 75 ? 'high' : confidencePercent >= 55 ? 'medium' : 'low'
  const breakdown = qualityResult.breakdown
  const concentration = { top5: 0, top10: 0, largest: 0, sector: 0, geography: 0, currency: 0, flags: ['TEFAS does not publish full position-level exposure in this feed.'] }
  const currentSignalNote = currentProviderProxy ? ' Exact 1W NAV is not in the universe payload, so a refreshed provider trend/momentum proxy is being used until direct TEFAS confirmation.' : ''
  return {
    momentum, momentumLabel: momentumLabel(momentum), momentumDirection, momentumNote,
    marketScore, marketLabel, marketSignal, relativeScore, riskAdjustedScore, riskScore, riskLabel: riskLabel(a.volatility1y), managerScore,
    cashFlowScore: 50, valuationScore: 50, diversificationScore: 50, concentration,
    qualityScore, entryScore, recommendation, recommendationLevel,
    recommendationNote: `Fund quality is ${qualityScore}/100 based on historical peer-relative data.${currentSignalNote} ${decisionGuard.status === 'clear' ? reasoning : `${decisionGuard.title}: ${decisionGuard.reasons[0] || 'current signals are not fully aligned'}`} The actionable stance is ${recommendation}.`,
    confidence, confidencePercent, dataQuality, breakdown,
    positives, risks, watchItems,
    decisionGuard,
    entryConsiderations: [decisionGuard.status === 'blocked' ? 'Do not add new money until the recent decline stabilizes and the trend proxy turns supportive again.' : decisionGuard.status === 'caution' ? 'Do not chase this fund. Wait for sector/current-signal confirmation; existing holders should monitor rather than automatically exit.' : currentProviderProxy ? 'Timing proxy is supportive, but confirm the direct TEFAS 1W return before placing an order; staged entries remain prudent.' : entryScore >= 70 ? 'Automatic timing signals are supportive, but staged entries remain prudent.' : 'Automatic timing is not exceptional; consider the current trend and drawdown context.'],
    exitTriggers: ['Review if the automatic stance deteriorates, peer percentile falls materially, or the trend phase turns negative.'],
    effectiveSharpe: a.sharpe1y, effectiveSortino: a.sortino1y,
    sharpeNote: a.sharpe1y !== null && a.sharpe1y > 10 ? 'Very high ratios can occur when downside volatility is tiny; compare within similar funds.' : undefined,
    sortinoNote: a.sortino1y !== null && a.sortino1y > 10 ? 'Very high ratios can occur when downside volatility is tiny; compare within similar funds.' : undefined,
    changes: [],
  }
}

export function calculateAnalysis(fund: Fund, funds: Fund[], settings: AppSettings, marketSignals: MarketSignal[] = []): FundAnalysis {
  if (fund.automatic) return automaticAnalysis(fund, fund.automatic, funds, marketSignals)
  const peers = categoryPeers(fund, funds)
  const peerReturns = (period: Period) => peers.map((peer) => peer.returns[period].fund)
  const currentReturns = fund.returns
  const momentumParts = (Object.keys(momentumWeights) as ('1m' | '3m' | '6m' | '1y')[]).map((period) => ({ period, score: metricPercentile(currentReturns[period].fund, peerReturns(period), true, 1.8), weight: momentumWeights[period] }))
  const momentum = Math.round(momentumParts.reduce((sum, item) => sum + item.score * item.weight, 0) / 100)
  const oneMonth = currentReturns['1m'].fund
  const threeMonthMonthly = currentReturns['3m'].fund === null ? null : currentReturns['3m'].fund / 3
  const sixMonthMonthly = currentReturns['6m'].fund === null ? null : currentReturns['6m'].fund / 6
  const momentumDirection: FundAnalysis['momentumDirection'] = oneMonth !== null && threeMonthMonthly !== null && sixMonthMonthly !== null && oneMonth < 0 && sixMonthMonthly > 0 ? 'reversal' : oneMonth !== null && threeMonthMonthly !== null && oneMonth < threeMonthMonthly * 0.75 ? 'decelerating' : oneMonth !== null && threeMonthMonthly !== null && oneMonth > threeMonthMonthly * 1.2 ? 'accelerating' : 'steady'
  const momentumNote = momentumDirection === 'reversal' ? 'Long-term performance remains positive, but recent momentum has weakened significantly.' : momentumDirection === 'decelerating' ? 'The medium-term trend is still constructive, but short-term returns are losing pace.' : momentumDirection === 'accelerating' ? 'Recent performance is stronger than the medium-term run rate.' : 'Short- and medium-term performance are broadly aligned.'

  const relativePeriods: Period[] = ['1m', '3m', '6m', '1y']
  const relativeParts = relativePeriods.map((period) => {
    const current = currentReturns[period].fund
    const relative = current === null || currentReturns[period].benchmark === null ? null : current - (currentReturns[period].benchmark ?? 0)
    const peerRelative = peers.map((peer) => peer.returns[period].fund !== null && peer.returns[period].benchmark !== null ? (peer.returns[period].fund ?? 0) - (peer.returns[period].benchmark ?? 0) : null)
    return metricPercentile(relative, peerRelative, true, 2)
  })
  const relativeScore = Math.round(average(relativeParts) ?? 50)

  const volPeer = peers.map((peer) => peer.volatility.y1)
  const ddPeer = peers.map((peer) => peer.drawdown.max)
  const downsidePeer = peers.map((peer) => peer.downsideRisk.fund)
  const volScore = metricPercentile(fund.volatility.y1, volPeer, false, -1.7)
  const ddScore = metricPercentile(fund.drawdown.max, ddPeer, true, 1.2)
  const downsideScore = metricPercentile(fund.downsideRisk.fund, downsidePeer, false, -1.5)
  const riskScore = Math.round((volScore * 0.35) + (ddScore * 0.4) + (downsideScore * 0.25))

  const effectiveSharpeValue = effectiveSharpe(fund)
  const effectiveSortinoValue = effectiveSortino(fund)
  const sharpeScore = metricPercentile(effectiveSharpeValue, peers.map((peer) => effectiveSharpe(peer)), true, 8)
  const sortinoScore = metricPercentile(effectiveSortinoValue, peers.map((peer) => effectiveSortino(peer)), true, 3)
  const riskAdjustedScore = Math.round((sharpeScore + sortinoScore) / 2)

  const managerScore = Math.round((relativeScore * 0.28) + (metricPercentile(fund.alpha.value, peers.map((peer) => peer.alpha.value), true, 2) * 0.22) + (metricPercentile(fund.drawdown.max, peers.map((peer) => peer.drawdown.max), true, 1) * 0.18) + (fund.managerInputs.consistency * 0.16) + (fund.managerInputs.rally * 0.08) + (fund.managerInputs.decline * 0.08))
  const cashFlowScore = cashScore(fund)
  const valuationInputs = [fund.valuation.pe, fund.valuation.pb, fund.valuation.roe, fund.valuation.earningsGrowth, fund.valuation.yield, fund.valuation.goldTrend, fund.valuation.realRates]
  const valuationScore = valuationInputs.some((value) => value !== null) ? clamp(50 + safe(fund.valuation.earningsGrowth) * 0.3 + safe(fund.valuation.roe) * 0.35 - safe(fund.valuation.pe) * 0.5 - safe(fund.valuation.pb) * 3 + safe(fund.valuation.goldTrend) * 8) : 50
  const concentration = calculateConcentration(fund, settings)
  const diversificationScore = clamp(100 - Math.max(0, concentration.top5 - settings.thresholds.topHoldings) * 2 - Math.max(0, concentration.sector - settings.thresholds.sector) * 1.5 - Math.max(0, concentration.largest - settings.thresholds.singleHolding) * 2)
  const marketAverage = average(fund.marketDrivers.map((driver) => driver.score)) ?? 0
  const marketScore = Math.round(clamp(50 + marketAverage * 25))
  const marketLabel = marketStatus(marketScore)

  const scores: Partial<Record<WeightKey, number>> = {}
  if (fund.marketDrivers.length) scores.market = marketScore
  if (relativePeriods.some((period) => currentReturns[period].fund !== null && currentReturns[period].benchmark !== null)) scores.relative = relativeScore
  if (['1m', '3m', '6m', '1y'].some((period) => currentReturns[period as Period].fund !== null)) scores.momentum = momentum
  if (effectiveSharpeValue !== null || effectiveSortinoValue !== null) scores.riskAdjusted = riskAdjustedScore
  if (fund.managerInputs.consistency !== 50 || fund.managerInputs.rally !== 50 || fund.managerInputs.decline !== 50 || fund.managerInputs.drawdownManagement !== 50 || fund.alpha.value !== null) scores.manager = managerScore
  if ([fund.cashFlow.daily, fund.cashFlow.weekly, fund.cashFlow.monthly, fund.cashFlow.fundSizeChange, fund.cashFlow.investorCountChange].some((value) => value !== null)) scores.cashFlow = cashFlowScore
  if (valuationInputs.some((value) => value !== null)) scores.valuation = valuationScore
  if (fund.exposure.asset.length || fund.holdings.length) scores.diversification = diversificationScore
  const composite = scoreWithWeights(scores, settings.weights)
  const qualityScore = composite.score
  const entryScore = Math.round(clamp(momentum * 0.28 + marketScore * 0.22 + (100 - Math.max(0, (oneMonth ?? 0) - 10) * 2) * 0.16 + valuationScore * 0.14 + cashFlowScore * 0.08 + ddScore * 0.12))
  const warnings: string[] = []
  if ((fund.drawdown.max ?? 0) <= -18) warnings.push('extreme drawdown')
  if (momentumDirection === 'reversal' || momentum < 42) warnings.push('momentum deterioration')
  if (marketScore < 40) warnings.push('weak underlying market')
  if (fund.cashFlow.monthly !== null && fund.cashFlow.monthly < -10) warnings.push('strong outflows')
  if (concentration.flags.length > 1) warnings.push('concentration risk')
  const recommendation = recommendationFor(qualityScore, entryScore, warnings.length)
  const positives: string[] = []
  const risks: string[] = []
  const watchItems: string[] = []
  const entryConsiderations: string[] = []
  const exitTriggers: string[] = []
  if (relativeScore >= 65) positives.push(`Outperforming the ${fund.category} peer set with a ${relativeScore}/100 relative score.`)
  if (momentum >= 65) positives.push(`${momentumLabel(momentum)} with recent returns still above the peer median.`)
  if (managerScore >= 70) positives.push(`Manager score is ${managerScore}/100, supported by consistency and peer-relative returns.`)
  if (marketScore >= 60) positives.push(`Underlying drivers lean ${marketLabel}; the market score is ${marketScore}/100.`)
  if (riskScore < 45) risks.push(`Risk profile is ${riskLabel(fund.volatility.y1)} and risk score is ${riskScore}/100.`)
  if ((fund.drawdown.max ?? 0) <= -12) risks.push(`Maximum drawdown is ${formatPct(fund.drawdown.max)}; compare mainly inside this category.`)
  if (fund.cashFlow.monthly !== null && fund.cashFlow.monthly < 0) risks.push(`Monthly flows are negative (${formatPct(fund.cashFlow.monthly)} of fund size).`)
  if (concentration.flags.length) risks.push(concentration.flags[0])
  if (momentumDirection === 'decelerating' || momentumDirection === 'reversal') watchItems.push(momentumNote)
  if (fund.sortino.fund !== null && Math.abs(fund.sortino.fund) > 10) watchItems.push('Sortino is unusually high; tiny downside deviation can inflate the ratio, especially for money market funds.')
  if (fund.beta.value !== null && fund.beta.value < 0) watchItems.push('Beta is negative; this is historical sensitivity, not a guaranteed inverse future response.')
  if ((fund.cashFlow.monthly ?? 0) > 10 && (oneMonth ?? 0) > 5 && momentum > 75) watchItems.push('Strong inflows may reflect growing popularity, but can also indicate late-stage performance chasing.')
  entryConsiderations.push(entryScore >= 70 ? 'Current entry conditions are supportive, subject to your risk tolerance.' : 'Entry timing is not exceptional; consider staged entries and the current drawdown context.')
  entryConsiderations.push(`Quality and timing are separate: fund quality is ${qualityScore}/100 while entry timing is ${entryScore}/100.`)
  exitTriggers.push(...fund.exitRules.filter((rule) => rule.enabled).map((rule) => rule.label))
  if (!exitTriggers.length) exitTriggers.push('No enabled user-defined exit rules yet.')
  const currentSnapshot = fund.history.at(-1)
  const previousSnapshot = fund.history.at(-2)
  const changes = previousSnapshot && currentSnapshot ? [difference(previousSnapshot.momentum, currentSnapshot.momentum, 'Momentum'), difference(previousSnapshot.score, currentSnapshot.score, 'Fund score'), difference(previousSnapshot.cashFlow, currentSnapshot.cashFlow, 'Cash flow', '%'), difference(previousSnapshot.marketScore, currentSnapshot.marketScore, 'Market score'), difference(previousSnapshot.drawdown, currentSnapshot.drawdown, 'Drawdown', '%')] : []
  const quality = dataQuality(fund, peers, settings)
  const agreement = Math.max(0, 100 - Math.abs(momentum - marketScore) * 0.45 - Math.abs(relativeScore - riskAdjustedScore) * 0.25)
  const confidencePercent = Math.round(clamp(quality * 0.65 + agreement * 0.35))
  const confidence: FundAnalysis['confidence'] = confidencePercent >= 75 ? 'high' : confidencePercent >= 55 ? 'medium' : 'low'
  const strongest = composite.breakdown.sort((a, b) => b.contribution - a.contribution).slice(0, 2).map((item) => item.label.toLowerCase()).join(' and ') || 'the available inputs'
  const recommendationNote = `${fund.code} currently receives a score of ${qualityScore}/100. The strongest contributors are ${strongest}. ${momentumDirection === 'decelerating' || momentumDirection === 'reversal' ? 'Recent momentum is losing pace. ' : ''}${marketScore < 50 ? 'Underlying market conditions are not fully supportive. ' : ''}The current setup suggests ${recommendation}, with ${confidence} confidence.`
  return {
    momentum, momentumLabel: momentumLabel(momentum), momentumDirection, momentumNote, marketScore, marketLabel, relativeScore, riskAdjustedScore, riskScore, riskLabel: riskLabel(fund.volatility.y1), managerScore, cashFlowScore, valuationScore, diversificationScore, concentration, qualityScore, entryScore, recommendation, recommendationLevel: recommendationLevel(recommendation), recommendationNote, confidence, confidencePercent, dataQuality: quality, breakdown: composite.breakdown, positives, risks, watchItems, entryConsiderations, exitTriggers, effectiveSharpe: effectiveSharpeValue, effectiveSortino: effectiveSortinoValue, sharpeNote: effectiveSharpeValue === null ? 'Sharpe is unavailable and excluded from the composite.' : undefined, sortinoNote: effectiveSortinoValue === null ? 'Sortino is unavailable and excluded from the composite.' : undefined, changes,
  }
}

export function calculateOverlap(first: Fund, second: Fund): { score: number; label: string; shared: { name: string; weight: number }[] } {
  const secondByName = new Map(second.holdings.map((holding) => [holding.name.toLowerCase(), holding.weight]))
  const shared = first.holdings.filter((holding) => secondByName.has(holding.name.toLowerCase())).map((holding) => ({ name: holding.name, weight: Math.min(holding.weight, secondByName.get(holding.name.toLowerCase()) ?? 0) }))
  const score = shared.reduce((sum, item) => sum + item.weight, 0)
  return { score, label: score >= 45 ? 'very high overlap' : score >= 25 ? 'high overlap' : score >= 10 ? 'moderate overlap' : 'low overlap', shared }
}

export function scenarioReaction(fund: Fund, scenario: { bist: number; usdtry: number; gold: number; nasdaq: number; rates: number }): number {
  const exposure = (name: string) => fund.exposure.asset.find((item) => item.name.toLowerCase().includes(name))?.value ?? 0
  const beta = safe(fund.beta.value, 0)
  const bistEffect = (scenario.bist * (exposure('turkish') / 100)) * beta
  const goldEffect = scenario.gold * (exposure('gold') / 100)
  const usdEffect = scenario.usdtry * ((exposure('usd') + exposure('gold')) / 100) * 0.35
  const rateEffect = -scenario.rates * ((exposure('government') + exposure('repo')) / 100) * 0.4
  const foreignEffect = scenario.nasdaq * (exposure('foreign') / 100) * 0.6
  return Number((bistEffect + goldEffect + usdEffect + rateEffect + foreignEffect).toFixed(1))
}

export function freshnessFor(date: string, settings: AppSettings): { label: 'fresh' | 'aging' | 'outdated'; days: number } {
  const days = Math.max(0, Math.round((new Date().getTime() - new Date(date).getTime()) / 86400000))
  return { days, label: days <= settings.freshness.freshDays ? 'fresh' : days <= settings.freshness.agingDays ? 'aging' : 'outdated' }
}
